/* eslint-disable react/jsx-no-bind */
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDrawingArea,
  selectDrawingPolygonArea
} from '@actions/map-actions';
import { mapConfig } from '@constants/component-configs';
import { useMap } from '@vis.gl/react-google-maps';
import TerraDrawReactGoogleMapsDrawingManager from '@shared/drawing';
import { getDrawingVisible, getSelectionDrawingMode } from '@selectors/map-selection';

const MapDrawingTools = () => {
  const dispatch = useDispatch();

  const map = useMap();

  const drawingMode = useSelector(getSelectionDrawingMode);
  const drawingVisible = useSelector(getDrawingVisible);

  const [mapsShape, setMapsShape] = useState(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    // Cleanup when unmounting:
    return () => mapsShape?.setMap(null);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const clearShape = () => {
    mapsShape?.setMap(null);
    setMapsShape(null);
    setActive(false);
  };

  const resetShape = () => {
    clearShape();
    setTimeout(() => setActive(true), 0);
  };

  useEffect(() => {
    if (drawingMode) {
      setActive(true);
    } else {
      clearShape();
    }
  }, [drawingMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (drawingMode) {
      resetShape();
    } else {
      clearShape();
    }
  }, [drawingVisible]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Memoized so TerraDrawReactGoogleMapsDrawingManager's internal `drawing` (a TerraDraw
  // instance, built from these via useMemo) doesn't get torn down and recreated on every
  // render - it previously got a brand new function reference each render, which stopped and
  // restarted the underlying TerraDraw instance constantly, racing its own `enabledDrawing`
  // state and occasionally throwing "Terra Draw is not enabled" from a setMode() call that
  // landed on an instance already torn down by a subsequent render's cleanup.
  const onCircleComplete = useCallback(shape => {
    setMapsShape(prevShape => {
      prevShape?.setMap(null);
      return shape;
    });
    setActive(false);
    dispatch(selectDrawingArea(shape.center, map, shape.radius, true));
  }, [dispatch, map]);

  const onPolygonComplete = useCallback(shape => {
    const shapeArray = shape.getPath().getArray();
    setMapsShape(prevShape => {
      prevShape?.setMap(null);
      return shapeArray.length < 3 ? null : shape;
    });
    // Delay deactivation so a double-click to finish drawing doesn't immediately trigger map zoom.
    setTimeout(() => setActive(false), 500);
    if (shapeArray.length < 3) {
      return;
    }
    dispatch(selectDrawingPolygonArea(shapeArray));
  }, [dispatch]);

  if (!Boolean(drawingMode.length)) {
    return null;
  }
  return (
    <TerraDrawReactGoogleMapsDrawingManager
      drawingMode={active ? drawingMode : null}
      options={mapConfig.drawingManager}
      onPolygonComplete={onPolygonComplete}
      onCircleComplete={onCircleComplete}
    />
  );
};

export default memo(MapDrawingTools);
